import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import eventService from "@/services/api/events";
import type { Event, GetEventsParams } from "@/services/api/events";

export interface EventMonthlyStats {
  totalSeatsThisMonth: number;
  seatsFilledThisMonth: number;
  ticketRevenueThisMonth: number;
  ticketRevenueLastMonth: number;
  hikePercentage: number;
  lastMonthName: string;
}

const initialMonthlyStats: EventMonthlyStats = {
  totalSeatsThisMonth: 0,
  seatsFilledThisMonth: 0,
  ticketRevenueThisMonth: 0,
  ticketRevenueLastMonth: 0,
  hikePercentage: 0,
  lastMonthName: "",
};

export interface EventState {
  events: Event[];
  totalEvents: number;
  monthlyStats: EventMonthlyStats;
  isFetching: boolean;
  error: string | null;
  // CreateGathering.tsx's edit flow — kept separate from `events` (the
  // list above) since it's sourced from a different endpoint
  // (`getEventById`, `GET /events/{eventId}`) with a richer shape (see
  // `Event.locations`'s own comment in services/api/events.ts) than the
  // list's items are confirmed to have.
  editingEvent: Event | null;
  isFetchingEditingEvent: boolean;
  editingEventError: string | null;
}

const initialState: EventState = {
  events: [],
  totalEvents: 0,
  monthlyStats: initialMonthlyStats,
  isFetching: false,
  error: null,
  editingEvent: null,
  isFetchingEditingEvent: false,
  editingEventError: null,
};

export const getEvents = createAsyncThunk(
  "event/getEvents",
  async (params: GetEventsParams | undefined) => eventService.getEvents(params),
);

export const getEventById = createAsyncThunk("event/getEventById", async (eventId: number) =>
  eventService.getEventById(eventId),
);

const eventSlice = createSlice({
  name: "event",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEvents.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getEvents.fulfilled, (state, action) => {
        state.isFetching = false;
        state.events = action.payload?.data?.results ?? [];
        state.totalEvents = action.payload?.data?.totalRecord ?? state.events.length;
        const data = action.payload?.data;
        state.monthlyStats = {
          totalSeatsThisMonth: data?.totalSeatsThisMonth ?? 0,
          seatsFilledThisMonth: data?.seatsFilledThisMonth ?? 0,
          ticketRevenueThisMonth: data?.ticketRevenueThisMonth ?? 0,
          ticketRevenueLastMonth: data?.ticketRevenueLastMonth ?? 0,
          hikePercentage: data?.hikePercentage ?? 0,
          lastMonthName: data?.lastMonthName ?? "",
        };
      })
      .addCase(getEvents.rejected, (state, action) => {
        state.isFetching = false;
        state.events = [];
        state.error = action.error.message ?? "Failed to load events";
      })
      .addCase(getEventById.pending, (state) => {
        state.isFetchingEditingEvent = true;
        state.editingEventError = null;
        // Cleared, not left in place, while the new fetch is in flight —
        // otherwise navigating from editing one gathering straight to
        // another (without a full remount) leaves the *previous* event's
        // data sitting here as a false "already loaded" value for however
        // long the new request takes, which is exactly what let
        // CreateGathering.tsx's edit-prefill effect fire once using the
        // wrong (stale) gathering's data and never re-fire once the real
        // one arrived (its `appliedEditRef` guard only allows one run per
        // mount) — see docs/decisions.md.
        state.editingEvent = null;
      })
      .addCase(getEventById.fulfilled, (state, action) => {
        state.isFetchingEditingEvent = false;
        state.editingEvent = action.payload?.data ?? null;
      })
      .addCase(getEventById.rejected, (state, action) => {
        state.isFetchingEditingEvent = false;
        state.editingEvent = null;
        state.editingEventError = action.error.message ?? "Failed to load gathering";
      });
  },
});

export default eventSlice.reducer;
